import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import axiosInstance from '../../../axiosInstance';
import Sidebar from '../../components/Sidebar';
import Navbar from '../../components/Navbar';

const getRelName = (value, fallback = 'NA') => {
  if (Array.isArray(value)) return value[1] || fallback;
  return value || fallback;
};

const getCurrency = (order) => getRelName(order?.currency_id, '$');

const formatDate = (dateString) => {
  if (!dateString) return 'NA';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatMoney = (value, currency) => `${currency} ${Number(value || 0).toFixed(2)}`;

const OdooOrderView = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const { data } = await axiosInstance.get(`/admin/odoo-order/${orderId}`);

        if (data?.success) {
          setOrder(data.order);
        } else {
          toast.error('Odoo order not found');
          navigate('/all-order');
        }
      } catch (error) {
        console.error('Error fetching Odoo order:', error);
        toast.error('Odoo order not found');
        navigate('/all-order');
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [navigate, orderId]);

  const currency = getCurrency(order);
  const lines = Array.isArray(order?.lines) ? order.lines : [];

  return (
    <>
      <Sidebar />
      <main className="content">
        <Navbar />

        <div className="pt-4 px-5">
          <nav aria-label="breadcrumb" className="d-none d-md-inline-block">
            <ol className="breadcrumb breadcrumb-dark breadcrumb-transparent">
              <li className="breadcrumb-item">
                <Link to="/dashboard">Admin</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/all-order">Orders</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                Odoo Order {order?.name || orderId}
              </li>
            </ol>
          </nav>

          <div className="d-flex justify-content-between w-100 flex-wrap">
            <Link
              to="/all-order"
              id="goBackButton"
              className="btn btn-primary d-inline-flex align-items-center"
            >
              <svg
                style={{ transform: 'rotate(180deg)' }}
                className="icon icon-sm"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
              Go back
            </Link>
          </div>
        </div>

        <div id="main-content" className="bg-transparent px-5 py-4">
          {loading ? (
            Array.from({ length: 2 }).map((_, index) => (
              <div className="skeleton mb-3" style={{ height: 154, borderRadius: 2 }} key={index} />
            ))
          ) : (
            <div className="card p-4">
              <div className="d-block d-lg-flex justify-content-between align-items-start border-bottom pb-3 mb-4">
                <div>
                  <h1 className="h4 mb-2">Odoo Order {order?.name}</h1>
                  <p className="mb-1"><b>Order Date:</b> {formatDate(order?.date_order)}</p>
                  <p className="mb-1"><b>Website Ref:</b> {order?.client_order_ref || 'NA'}</p>
                  <p className="mb-0"><b>Origin:</b> {order?.origin || 'NA'}</p>
                </div>

                <div className="text-lg-end mt-3 mt-lg-0">
                  <span className="badge rounded-pill bg-info px-3 py-2">{order?.state || 'NA'}</span>
                  <p className="mb-1 mt-3"><b>Invoice:</b> {order?.invoice_status || 'NA'}</p>
                  <p className="mb-0"><b>Customer:</b> {getRelName(order?.partner_id, 'Odoo Customer')}</p>
                </div>
              </div>

              <div className="table-responsive">
                <table className="table table-flush">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Description</th>
                      <th>Quantity</th>
                      <th>Unit Price</th>
                      <th>Tax</th>
                      <th className="text-end">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.length ? lines.map((line) => (
                      <tr key={line.id}>
                        <td>{getRelName(line.product_id)}</td>
                        <td style={{ whiteSpace: 'break-spaces', maxWidth: 360 }}>{line.name}</td>
                        <td>{line.product_uom_qty}</td>
                        <td>{formatMoney(line.price_unit, currency)}</td>
                        <td>{formatMoney(line.price_tax, currency)}</td>
                        <td className="text-end">{formatMoney(line.price_total, currency)}</td>
                      </tr>
                    )) : (
                      <tr>
                        <td colSpan={6} className="text-center text-muted">No line items found</td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={5} className="text-end fw-bold">Subtotal</td>
                      <td className="text-end">{formatMoney(order?.amount_untaxed, currency)}</td>
                    </tr>
                    <tr>
                      <td colSpan={5} className="text-end fw-bold">Tax</td>
                      <td className="text-end">{formatMoney(order?.amount_tax, currency)}</td>
                    </tr>
                    <tr>
                      <td colSpan={5} className="text-end fw-bold">Total</td>
                      <td className="text-end fw-bold">{formatMoney(order?.amount_total, currency)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
};

export default OdooOrderView;
